import { Row, RowData, RowSelectionState } from '@tanstack/react-table'
import { useCallback, useRef } from 'react'

export function useRangeSelectionHandler<T extends RowData>(
  setRowSelection:
    ((updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => void) | undefined,
) {
  const anchorIndexRef = useRef<number | null>(null)
  const focusIndexRef = useRef<number | null>(null)
  const prevRangeIdsRef = useRef<Set<string>>(new Set())
  const anchorIntentRef = useRef<boolean>(true) // true = selecting, false = deselecting

  const resetAnchor = useCallback(() => {
    anchorIndexRef.current = null
    focusIndexRef.current = null
    prevRangeIdsRef.current = new Set()
    anchorIntentRef.current = true
  }, [])

  const handleRowClick = useCallback(
    (rowIndex: number, rowId: string, shiftKey: boolean, allRows: Row<T>[]) => {
      if (!setRowSelection) return

      // ── Plain click ──────────────────────────────────────────────────────
      if (!shiftKey || anchorIndexRef.current === null) {
        anchorIndexRef.current = rowIndex
        focusIndexRef.current = rowIndex
        prevRangeIdsRef.current = new Set()

        setRowSelection((prev) => {
          const next = { ...prev }
          if (next[rowId]) {
            delete next[rowId]
            anchorIntentRef.current = false // was selected → intent is to deselect
          } else {
            next[rowId] = true
            anchorIntentRef.current = true // was unselected → intent is to select
          }

          return next
        })

        return
      }

      // ── Shift-click ──────────────────────────────────────────────────────
      const prevFocus = focusIndexRef.current!
      const anchor = anchorIndexRef.current

      const isCrossing = (prevFocus > anchor && rowIndex < anchor) || (prevFocus < anchor && rowIndex > anchor)

      if (isCrossing) {
        anchorIndexRef.current = prevFocus
      }

      const isShrinking =
        !isCrossing && prevFocus !== anchor && Math.abs(rowIndex - anchor) < Math.abs(prevFocus - anchor)

      focusIndexRef.current = rowIndex

      const currentAnchor = anchorIndexRef.current
      const rangeStart = Math.min(currentAnchor, rowIndex)
      const rangeEnd = Math.max(currentAnchor, rowIndex)

      const newRangeIds = new Set<string>()
      for (let i = rangeStart; i <= rangeEnd; i++) {
        if (isShrinking && i === rowIndex) continue
        const r = allRows[i]
        if (r) newRangeIds.add(r.id)
      }

      const prevRangeIds = prevRangeIdsRef.current
      prevRangeIdsRef.current = newRangeIds

      const intent = anchorIntentRef.current // capture for use inside updater

      setRowSelection((prev) => {
        const next = { ...prev }

        // Restore rows that left the range back to pre-range state
        prevRangeIds.forEach((id) => {
          if (!newRangeIds.has(id)) {
            if (intent) {
              delete next[id] // we were selecting → undo = deselect
            } else {
              next[id] = true // we were deselecting → undo = reselect
            }
          }
        })

        // Apply intent to rows entering the range
        newRangeIds.forEach((id) => {
          if (intent) {
            next[id] = true
          } else {
            delete next[id]
          }
        })

        return next
      })
    },
    [setRowSelection],
  )

  return { handleRowClick, resetAnchor }
}
